// src/admin/tabs/PartnersTab2.js

import React, { useState, useEffect } from 'react';

const PartnersTab2 = () => {
    // --- STATE ---
    const [partners, setPartners] = useState([]);
    const [name, setName] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    // --- FETCH DATA ---
    const fetchPartners = () => {
        // Nayi API Endpoint use ho rahi hai
        fetch('/api/school-partners')
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setPartners(data);
                    setFetchError(null);
                } else {
                    setFetchError("Invalid data received from server");
                }
            })
            .catch(err => {
                console.error(err);
                setFetchError("Failed to connect to server");
            });
    };

    // Load data on component mount
    useEffect(() => {
        fetchPartners();
    }, []);

    // --- HANDLERS ---
    
    // 1. Add New Partner
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!name || !file) {
            alert("Please provide both School Name and Logo Image.");
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('logo', file); 

        setLoading(true);
        try {
            // Nayi API Endpoint POST ke liye
            const res = await fetch('/api/school-partners', {
                method: 'POST',
                body: formData,
            });

            if (res.ok) {
                // Reset Form
                setName('');
                setFile(null);
                document.getElementById('logoInput').value = ""; 
                fetchPartners(); // Refresh Grid
            } else {
                alert("Failed to upload. Please try again.");
            }
        } catch (error) {
            console.error("Error adding partner:", error);
            alert("Server Error");
        }
        setLoading(false);
    };

    // 2. Delete Partner
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to remove this school?")) return;

        try {
            // UPDATED: Ab ye delete request bhi nayi API par jayegi
            const res = await fetch(`/api/school-partners/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                fetchPartners(); // Refresh Grid
            } else {
                alert("Failed to delete.");
            }
        } catch (error) {
            console.error("Error deleting partner:", error);
        }
    };

    return (
        <div className="space-y-8">
            {/* SECTION 1: ADD NEW PARTNER FORM */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-800 mb-4 uppercase tracking-wide">
                    Add New School (V2)
                </h3>
                
                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end">
                    
                    {/* Name Input */}
                    <div className="w-full md:w-1/3">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">School Name</label>
                        <input 
                            type="text" 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. St. Paul's School"
                            className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm font-medium"
                        />
                    </div>

                    {/* File Input */}
                    <div className="w-full md:w-1/3">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Upload Logo</label>
                        <input 
                            id="logoInput"
                            type="file" 
                            accept="image/*"
                            onChange={(e) => setFile(e.target.files[0])}
                            className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100 transition-colors cursor-pointer"
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="w-full md:w-auto">
                        <button 
                            type="submit" 
                            disabled={loading}
                            className={`w-full md:w-auto px-6 py-3 rounded-lg font-bold text-sm text-white shadow-md transition-transform active:scale-95 ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Uploading...
                                </span>
                            ) : (
                                "+ Add Partner"
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* SECTION 2: PARTNERS GRID */}
            <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 uppercase tracking-wide flex justify-between items-center">
                    Current Partners <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-md">{partners.length} Total</span>
                </h3>

                {fetchError && (
                    <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm font-bold text-center border border-red-100">
                        {fetchError}
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {partners.length > 0 ? (
                        partners.map((partner) => (
                            <div key={partner.id} className="group relative bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center gap-4 hover:shadow-lg transition-all duration-300">
                                
                                {/* Image Container */}
                                <div className="h-20 w-full flex items-center justify-center p-2 bg-slate-50 rounded-lg">
                                    <img 
                                        src={partner.logo_path} 
                                        alt={partner.name} 
                                        className="max-h-full max-w-full object-contain"
                                        onError={(e) => {e.target.src = 'https://via.placeholder.com/150?text=No+Img'}} 
                                    />
                                </div>
                                
                                {/* Name */}
                                <p className="text-xs font-bold text-center text-slate-600 uppercase tracking-tight line-clamp-1">
                                    {partner.name}
                                </p>

                                {/* Delete Button (Visible on Hover) */}
                                <button 
                                    onClick={() => handleDelete(partner.id)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white w-8 h-8 rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:scale-110 transition-all duration-200"
                                    title="Delete Partner"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                </button>
                            </div>
                        ))
                    ) : (
                        !loading && (
                            <div className="col-span-full py-12 flex flex-col items-center text-slate-400">
                                <svg className="w-16 h-16 mb-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                <p className="text-sm font-bold">No partners found.</p>
                                <p className="text-xs">Add a school using the form above.</p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnersTab2;